import type { FormQuestion } from "./questions";

export type School = "Suttons Bay" | "Northport" | "Leland" | "Glen Lake";

export type StaffMember = {
  id: string;
  given: string;
  family: string;
  email: string;
};

/** Staff identity for the prototype gate and the QA staff login. */
export const staff: StaffMember = {
  id: "audrey",
  given: "Audrey",
  family: "Luksch",
  email: "audluksch@liftteencenter.com",
};

export function staffEmail(member: StaffMember = staff): string {
  return member.email.trim().toLowerCase();
}

/** Contact line included in family confirmation emails. */
export const liftContact = {
  name: "Lift · Leelanau Investing For Teens",
  email: "systems@liftteencenter.com",
  phone: "(231) 555-0148",
};

export const DEMO_PARENT_EMAIL = "julia.koleno@example.test";

export type Student = {
  id: string;
  given: string;
  family: string;
  email: string;
  school: School;
  grade: number;
  age: number;
  birthday: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  emergencyName: string;
  emergencyPhone: string;
  /** Missing means the child is already on the account. Pending waits for the parent to accept. */
  acceptance?: "pending" | "accepted" | "declined";
};

export type EventStatus = "open" | "archived";
export type EventKind = "rsvp" | "program";
export type RegistrationStatus = "registered" | "waitlisted" | "cancelled";
export type AgeBand = "ELEM" | "MS" | "HS";
export type AgeFilter = "all" | "ms" | "hs";

export type LiftEvent = {
  id: string;
  slug: string;
  name: string;
  dateIso: string;
  dateLabel: string;
  timeLabel: string;
  location: string;
  capacity: number;
  registered: number;
  schools: School[] | "all";
  age: AgeFilter;
  status: EventStatus;
  kind: EventKind;
  waitlistEnabled: boolean;
  questions: FormQuestion[];
  description: string;
  descriptionEs: string;
};

export type Registration = {
  id: string;
  eventId: string;
  studentId: string;
  status: RegistrationStatus;
  registeredAt: string;
  waitlistedAt: string | null;
  cancelledAt: string | null;
  checkedInAt: string | null;
  checkedInBy: string | null;
  answers: Record<string, string>;
};

/** Pre-v6 localStorage attendance rows (registration + door status mixed). */
export type LegacyAttendanceRow = {
  studentId: string;
  status: "checked_in" | "registered" | "no_show";
  at: string | null;
};

/** Deterministic opaque ids for seed/migrated registrations (not student ids). */
export function opaqueRegistrationId(eventId: string, studentId: string): string {
  const input = `${eventId}:${studentId}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `r${(hash >>> 0).toString(36)}`;
}

export const schools: School[] = [
  "Suttons Bay",
  "Northport",
  "Leland",
  "Glen Lake",
];

export function emptySchoolSelection(): Record<School, boolean> {
  return {
    "Suttons Bay": false,
    Northport: false,
    Leland: false,
    "Glen Lake": false,
  };
}

export function todayIso(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Door time stored as an ISO timestamp, or the prototype's clock label. */
export function formatCheckInAt(value: string | null): string {
  if (!value) return "-";
  if (!value.includes("T")) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateLabel(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function schoolSlug(school: School): string {
  return school.toLowerCase().replace(/\s+/g, "-");
}

/** Families only see upcoming RSVP events. Programs stay staff-only. Past dates drop off. */
export function eventHiddenFromFamilies(
  event: LiftEvent,
  today = todayIso(),
): boolean {
  if (event.kind === "program") return true;
  return event.status === "archived" || event.dateIso < today;
}

/** Numeric codes match the 2026-2027 permission form, including early grades. */
export const GRADE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: -3, label: "Head Start" },
  { value: -2, label: "Pre-K" },
  { value: -1, label: "TK" },
  { value: 0, label: "K" },
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
];

export const grades = GRADE_OPTIONS.map((item) => item.value);

const EARLY_GRADE_AGE: Record<number, number> = {
  [-3]: 4,
  [-2]: 4,
  [-1]: 5,
  0: 5,
};

export function gradeLabel(grade: number): string {
  return GRADE_OPTIONS.find((item) => item.value === grade)?.label ?? String(grade);
}

export function ageForGrade(grade: number): number {
  return EARLY_GRADE_AGE[grade] ?? grade + 5;
}

/** Stable fake birthday for seed students (school year 2026-2027). */
export function birthdayFromId(id: string, grade: number): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash += id.charCodeAt(i) * (i + 1);
  }
  const year = 2026 - ageForGrade(grade);
  const month = (hash % 12) + 1;
  const day = (hash % 27) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function formatBirthdayLabel(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function parseBirthday(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return toIsoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }
  const us = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (us) {
    let year = Number(us[3]);
    if (year < 100) year += year >= 50 ? 1900 : 2000;
    return toIsoDate(year, Number(us[1]), Number(us[2]));
  }
  return null;
}

function toIsoDate(year: number, month: number, day: number): string | null {
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function normalizePersonName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function nameBirthdayKey(
  given: string,
  family: string,
  birthday: string,
): string {
  return `${normalizePersonName(given)}|${normalizePersonName(family)}|${birthday}`;
}

export function findStudentByNameAndBirthday(
  list: Student[],
  given: string,
  family: string,
  birthday: string,
): Student | undefined {
  if (!birthday) return undefined;
  const key = nameBirthdayKey(given, family, birthday);
  return list.find(
    (student) =>
      student.birthday &&
      nameBirthdayKey(student.given, student.family, student.birthday) === key,
  );
}

/** Head Start through 5th is elementary. 6th-8th is MS. 9th-12th is HS. */
export function ageBandForGrade(grade: number): AgeBand {
  if (grade <= 5) return "ELEM";
  if (grade <= 8) return "MS";
  return "HS";
}

export function matchesAgeFilter(grade: number, filter: AgeFilter): boolean {
  if (filter === "all") return true;
  if (filter === "ms") return grade >= 6 && grade <= 8;
  return grade >= 9;
}

export function ageAudienceLabel(filter: AgeFilter): string {
  if (filter === "ms") return "Middle school";
  if (filter === "hs") return "High school";
  return "All ages";
}

export function schoolAudienceLabel(event: LiftEvent): string {
  return event.schools === "all"
    ? "All Lift schools"
    : event.schools.join(" + ");
}

export function eventAudienceLabel(event: LiftEvent): string {
  return `${schoolAudienceLabel(event)} · ${ageAudienceLabel(event.age)}`;
}

export function eventDescription(
  event: Pick<LiftEvent, "description" | "descriptionEs">,
  locale: "en" | "es" = "en",
): string {
  if (locale === "es" && event.descriptionEs.trim()) {
    return event.descriptionEs.trim();
  }
  return event.description.trim();
}

function mockStudent(
  id: string,
  given: string,
  family: string,
  school: School,
  grade: number,
  guardianGiven: string,
  phone: string,
): Student {
  const email = `${given}.${family}@example.test`.toLowerCase();
  const guardianName = `${guardianGiven} ${family}`;
  const guardianEmail = `${guardianGiven}.${family}@example.test`.toLowerCase();
  return {
    id,
    given,
    family,
    email,
    school,
    grade,
    age: ageForGrade(grade),
    birthday: birthdayFromId(id, grade),
    guardianName,
    guardianEmail,
    guardianPhone: phone,
    emergencyName: guardianName,
    emergencyPhone: phone,
  };
}

/** Fake standalone students only. */
export const students: Student[] = [
  mockStudent("mike", "Mike", "Smith", "Suttons Bay", 9, "Mary", "(231) 555-0101"),
  mockStudent("sue", "Sue", "Jones", "Suttons Bay", 10, "Bill", "(231) 555-0102"),
  mockStudent("tom", "Tom", "Brown", "Northport", 9, "Carol", "(231) 555-0201"),
  mockStudent("ann", "Ann", "Wilson", "Northport", 11, "Dave", "(231) 555-0202"),
  mockStudent("dan", "Dan", "Davis", "Leland", 10, "Lisa", "(231) 555-0301"),
  mockStudent("amy", "Amy", "Miller", "Leland", 12, "Bob", "(231) 555-0302"),
  mockStudent("jim", "Jim", "Clark", "Suttons Bay", 6, "Ruth", "(231) 555-0103"),
  mockStudent("kate", "Kate", "Baker", "Suttons Bay", 7, "Ed", "(231) 555-0104"),
  mockStudent("ben", "Ben", "White", "Suttons Bay", 8, "Joan", "(231) 555-0105"),
  mockStudent("lucy", "Lucy", "Hall", "Suttons Bay", 11, "Frank", "(231) 555-0106"),
  mockStudent("jack", "Jack", "Young", "Suttons Bay", 12, "Doris", "(231) 555-0107"),
  mockStudent("peg", "Peg", "King", "Suttons Bay", 8, "Al", "(231) 555-0108"),
  mockStudent("tim", "Tim", "Allen", "Northport", 6, "Helen", "(231) 555-0203"),
  mockStudent("nancy", "Nancy", "Scott", "Northport", 7, "Carl", "(231) 555-0204"),
  mockStudent("paul", "Paul", "Green", "Northport", 8, "Betty", "(231) 555-0205"),
  mockStudent("beth", "Beth", "Adams", "Northport", 10, "Ron", "(231) 555-0206"),
  mockStudent("ray", "Ray", "Nelson", "Northport", 12, "Jean", "(231) 555-0207"),
  mockStudent("judy", "Judy", "Hill", "Northport", 8, "Don", "(231) 555-0208"),
  mockStudent("joe", "Joe", "Carter", "Leland", 6, "Karen", "(231) 555-0303"),
  mockStudent("jane", "Jane", "Moore", "Leland", 7, "Phil", "(231) 555-0304"),
  mockStudent("rick", "Rick", "Taylor", "Leland", 8, "Diane", "(231) 555-0305"),
  mockStudent("sally", "Sally", "Martin", "Leland", 11, "Gary", "(231) 555-0306"),
  mockStudent("greg", "Greg", "Anderson", "Leland", 9, "Nancy", "(231) 555-0307"),
  mockStudent("helen", "Helen", "Wright", "Leland", 12, "Stan", "(231) 555-0308"),
  mockStudent("bill", "Bill", "Cook", "Glen Lake", 6, "Pat", "(231) 555-0401"),
  mockStudent("mary", "Mary", "Price", "Glen Lake", 7, "Ted", "(231) 555-0402"),
  mockStudent("ted", "Ted", "Ross", "Glen Lake", 8, "Cindy", "(231) 555-0403"),
  mockStudent("pat", "Pat", "Cole", "Glen Lake", 9, "Lynn", "(231) 555-0404"),
  mockStudent("cindy", "Cindy", "West", "Glen Lake", 10, "Mark", "(231) 555-0405"),
  mockStudent("nick", "Nick", "Gray", "Glen Lake", 11, "Susan", "(231) 555-0406"),
  mockStudent("diane", "Diane", "Fox", "Glen Lake", 12, "Bruce", "(231) 555-0407"),
  mockStudent("steve", "Steve", "Lane", "Glen Lake", 8, "Cathy", "(231) 555-0408"),
  mockStudent("cece", "Cecilia", "Koleno", "Suttons Bay", 11, "Julia", "(773) 555-7448"),
  mockStudent("nelson", "Nelson", "Koleno", "Suttons Bay", 9, "Julia", "(773) 555-7448"),
];

function liftEvent(
  id: string,
  name: string,
  dateIso: string,
  timeLabel: string,
  location: string,
  capacity: number,
  schools: School[] | "all",
  kind: EventKind = "rsvp",
  questions: FormQuestion[] = [],
  age: AgeFilter = "all",
  description = "",
  descriptionEs = "",
): LiftEvent {
  return {
    id,
    slug: id,
    name,
    dateIso,
    dateLabel: formatDateLabel(dateIso),
    timeLabel,
    location,
    capacity,
    registered: 0,
    schools,
    age,
    status: "open",
    kind,
    waitlistEnabled: kind !== "program",
    questions,
    description,
    descriptionEs,
  };
}

const eventSeeds: LiftEvent[] = [
  liftEvent(
    "fall-activity-day",
    "Lift Fall Activity Day",
    "2026-09-26",
    "1:00-4:00 PM",
    "Lift Community Center",
    40,
    "all",
    "rsvp",
    [
      {
        id: "q-ride",
        label: "Need a van ride?",
        labelEs: "¿Necesita transporte en van?",
        kind: "yesno",
        required: true,
        options: [],
        optionsEs: [],
      },
      {
        id: "q-activity",
        label: "First-choice activity",
        labelEs: "Actividad de primera opción",
        kind: "radio",
        required: true,
        options: ["Gym games", "Art room", "Outdoor"],
        optionsEs: ["Juegos en el gimnasio", "Sala de arte", "Aire libre"],
      },
    ],
  ),
  liftEvent(
    "saturday-hike",
    "Saturday Hike",
    "2026-10-18",
    "10:00 AM-1:00 PM",
    "Sleeping Bear trailhead",
    12,
    "all",
    "rsvp",
    [],
    "ms",
    "Wear sturdy shoes and bring water. This is a moderate trail walk.",
    "Deben usar zapatos resistentes y traer agua. Es una caminata moderada.",
  ),
  liftEvent(
    "beach-day",
    "Beach Day",
    "2026-10-31",
    "11:00 AM-3:00 PM",
    "West End Beach",
    20,
    "all",
    "rsvp",
    [],
    "all",
    "Students attending this beach day have to be strong swimmers. Life jackets are not provided.",
    "Los estudiantes que asistan a este día de playa deben ser buenos nadadores. No se proporcionan chalecos salvavidas.",
  ),
  liftEvent(
    "northport-movie-night",
    "Northport Movie Night",
    "2026-10-24",
    "6:00-8:30 PM",
    "Northport School gym",
    22,
    ["Northport"],
  ),
  liftEvent(
    "pizza-friday",
    "Pizza Friday",
    "2026-11-07",
    "3:30-5:30 PM",
    "Leland School cafeteria",
    26,
    ["Leland", "Glen Lake"],
  ),
  liftEvent(
    "sleeping-bear-van",
    "Sleeping Bear van trip",
    "2026-09-05",
    "9:00 AM-3:00 PM",
    "Sleeping Bear",
    12,
    "all",
  ),
  liftEvent(
    "welcome-kickoff",
    "Welcome kickoff hike",
    "2026-08-28",
    "10:00 AM-12:00 PM",
    "Lift Community Center",
    40,
    "all",
  ),
  liftEvent(
    "suttons-after-school",
    "Suttons Bay After School",
    "2026-08-10",
    "3:15-5:30 PM",
    "Suttons Bay School",
    28,
    ["Suttons Bay"],
    "program",
  ),
];

export function studentsBySchool(school: School): Student[] {
  return students.filter((s) => s.school === school);
}

export function getStudent(id: string): Student | undefined {
  return students.find((s) => s.id === id);
}

export function getEvent(id: string): LiftEvent | undefined {
  return events.find((e) => e.id === id);
}

export function eventOpenToSchool(event: LiftEvent, school: School): boolean {
  return event.schools === "all" || event.schools.includes(school);
}

export function eventOpenToAge(event: LiftEvent, grade: number): boolean {
  return matchesAgeFilter(grade, event.age);
}

export function eventOpenToStudent(
  event: LiftEvent,
  student: Pick<Student, "school" | "grade">,
): boolean {
  return (
    eventOpenToSchool(event, student.school) &&
    eventOpenToAge(event, student.grade)
  );
}

/** Students who can RSVP. Others stay off the choice list. */
export function studentsOpenForEvent<T extends Pick<Student, "school" | "grade">>(
  event: LiftEvent,
  students: readonly T[],
): T[] {
  return students.filter((student) => eventOpenToStudent(event, student));
}

export function eventMatchesAgeFilter(
  event: LiftEvent,
  filter: AgeFilter,
): boolean {
  if (filter === "all") return true;
  return event.age === filter;
}

export function eventMatchesSchoolFilter(
  event: LiftEvent,
  school: School | "all",
): boolean {
  if (school === "all") return true;
  return eventOpenToSchool(event, school);
}

function seedRegistration(
  eventId: string,
  studentId: string,
  checkedInAt: string | null = null,
  answers: Record<string, string> = {},
): Registration {
  return {
    id: opaqueRegistrationId(eventId, studentId),
    eventId,
    studentId,
    status: "registered",
    registeredAt: "2026-09-01T16:00:00.000Z",
    waitlistedAt: null,
    cancelledAt: null,
    checkedInAt,
    checkedInBy: checkedInAt ? staff.id : null,
    answers,
  };
}

/** Native seed registrations (students only). */
export const seedRegistrations: Record<string, Registration[]> = {
  "fall-activity-day": [
    seedRegistration("fall-activity-day", "mike", "12:58 PM", {
      "q-ride": "no",
      "q-activity": "Gym games",
    }),
    seedRegistration("fall-activity-day", "sue", null, {
      "q-ride": "yes",
      "q-activity": "Art room",
    }),
    seedRegistration("fall-activity-day", "tom", "1:04 PM"),
    seedRegistration("fall-activity-day", "ann"),
    seedRegistration("fall-activity-day", "dan"),
    seedRegistration("fall-activity-day", "amy"),
    seedRegistration("fall-activity-day", "jim"),
    seedRegistration("fall-activity-day", "kate", "1:07 PM"),
    seedRegistration("fall-activity-day", "ben"),
    seedRegistration("fall-activity-day", "tim"),
    seedRegistration("fall-activity-day", "nancy"),
    seedRegistration("fall-activity-day", "paul", "1:11 PM"),
    seedRegistration("fall-activity-day", "joe"),
    seedRegistration("fall-activity-day", "jane"),
    seedRegistration("fall-activity-day", "bill", "1:09 PM"),
    seedRegistration("fall-activity-day", "mary"),
    seedRegistration("fall-activity-day", "ted"),
    seedRegistration("fall-activity-day", "cindy"),
  ],
  "saturday-hike": [
    seedRegistration("saturday-hike", "jim"),
    seedRegistration("saturday-hike", "kate", "10:02 AM"),
    seedRegistration("saturday-hike", "ben"),
    seedRegistration("saturday-hike", "peg"),
    seedRegistration("saturday-hike", "tim"),
    seedRegistration("saturday-hike", "nancy"),
    seedRegistration("saturday-hike", "paul"),
    seedRegistration("saturday-hike", "joe"),
    seedRegistration("saturday-hike", "jane"),
    seedRegistration("saturday-hike", "rick"),
    seedRegistration("saturday-hike", "steve", "10:05 AM"),
  ],
  "sleeping-bear-van": [
    seedRegistration("sleeping-bear-van", "mike", "8:54 AM"),
    seedRegistration("sleeping-bear-van", "sue", "8:56 AM"),
    seedRegistration("sleeping-bear-van", "tom", "8:58 AM"),
    seedRegistration("sleeping-bear-van", "ann"),
    seedRegistration("sleeping-bear-van", "kate", "9:01 AM"),
    seedRegistration("sleeping-bear-van", "bill", "8:57 AM"),
    seedRegistration("sleeping-bear-van", "pat"),
    seedRegistration("sleeping-bear-van", "steve"),
  ],
  "welcome-kickoff": [
    seedRegistration("welcome-kickoff", "mike", "9:58 AM"),
    seedRegistration("welcome-kickoff", "sue", "10:01 AM"),
    seedRegistration("welcome-kickoff", "jim", "10:04 AM"),
    seedRegistration("welcome-kickoff", "tom", "10:02 AM"),
    seedRegistration("welcome-kickoff", "dan"),
    seedRegistration("welcome-kickoff", "bill", "10:06 AM"),
    seedRegistration("welcome-kickoff", "mary"),
  ],
  "northport-movie-night": [
    seedRegistration("northport-movie-night", "tom"),
    seedRegistration("northport-movie-night", "ann", "6:05 PM"),
    seedRegistration("northport-movie-night", "tim"),
    seedRegistration("northport-movie-night", "nancy"),
    seedRegistration("northport-movie-night", "paul"),
    seedRegistration("northport-movie-night", "beth"),
    seedRegistration("northport-movie-night", "ray"),
    seedRegistration("northport-movie-night", "judy", "6:10 PM"),
  ],
  "suttons-after-school": [
    seedRegistration("suttons-after-school", "mike"),
    seedRegistration("suttons-after-school", "sue"),
    seedRegistration("suttons-after-school", "jim"),
    seedRegistration("suttons-after-school", "kate"),
    seedRegistration("suttons-after-school", "ben"),
    seedRegistration("suttons-after-school", "lucy"),
    seedRegistration("suttons-after-school", "jack"),
    seedRegistration("suttons-after-school", "peg"),
  ],
  "pizza-friday": [
    seedRegistration("pizza-friday", "dan"),
    seedRegistration("pizza-friday", "amy"),
    seedRegistration("pizza-friday", "joe", "3:34 PM"),
    seedRegistration("pizza-friday", "jane"),
    seedRegistration("pizza-friday", "sally"),
    seedRegistration("pizza-friday", "greg"),
    seedRegistration("pizza-friday", "helen"),
    seedRegistration("pizza-friday", "bill"),
    seedRegistration("pizza-friday", "mary", "3:36 PM"),
    seedRegistration("pizza-friday", "cindy"),
    seedRegistration("pizza-friday", "diane"),
  ],
};

export type SessionMark = "present" | "absent";

export type EventSessionRow = {
  studentId: string;
  status: SessionMark;
  at: string | null;
};

export type EventAttendanceSession = {
  id: string;
  eventId: string;
  dateIso: string;
  dateLabel: string;
  savedAt: string;
  rows: EventSessionRow[];
};

export function sessionKey(eventId: string, dateIso: string): string {
  return `${eventId}::${dateIso}`;
}

function sessionRow(
  studentId: string,
  status: SessionMark,
  at: string | null = null,
): EventSessionRow {
  return { studentId, status, at };
}

function seedSession(
  eventId: string,
  dateIso: string,
  present: string[],
  absent: string[],
): EventAttendanceSession {
  return {
    id: sessionKey(eventId, dateIso),
    eventId,
    dateIso,
    dateLabel: formatDateLabel(dateIso),
    savedAt: `${formatDateLabel(dateIso)} · 5:32 PM`,
    rows: [
      ...present.map((id) => sessionRow(id, "present", "3:22 PM")),
      ...absent.map((id) => sessionRow(id, "absent")),
    ],
  };
}

/** Dated present/absent sessions under an event (staff attendance). */
export const seedSessions: EventAttendanceSession[] = [
  seedSession(
    "suttons-after-school",
    "2026-09-09",
    ["mike", "sue", "jim", "kate", "ben", "peg"],
    ["jack", "lucy"],
  ),
  seedSession(
    "suttons-after-school",
    "2026-09-10",
    ["mike", "sue", "kate", "ben", "lucy", "peg"],
    ["jim", "jack"],
  ),
  seedSession(
    "suttons-after-school",
    "2026-09-11",
    ["mike", "kate", "ben"],
    ["jack"],
  ),
  seedSession(
    "sleeping-bear-van",
    "2026-09-05",
    ["mike", "sue", "tom", "kate", "bill"],
    ["ann", "steve"],
  ),
];

export const events: LiftEvent[] = eventSeeds.map((event) => ({
  ...event,
  registered: (seedRegistrations[event.id] ?? []).filter(
    (row) => row.status === "registered",
  ).length,
}));
